import React from 'react';
import styles from '../admin-dashboard.module.scss';

type MetricData = {
  name: string;
  percentage: number;
  color: string;
};

type Props = {
  metrics: MetricData[];
};

const ViviHealthScoreGraph: React.FC<Props> = ({ metrics }) => {
  const radius = 60;
  const circumference = 2 * Math.PI * radius;

  // Predefined colors for metrics
  const metricColors = [
    '#4A90E2', // Blue
    '#7ED321', // Green
    '#F5A623', // Orange
    '#D0021B', // Red
    '#9013FE', // Purple
    '#50E3C2', // Teal
    '#B8E986', // Light Green
  ];

  // Assign colors to metrics
  const metricsWithColors = metrics.map((metric, index) => ({
    ...metric,
    color: metric.color || metricColors[index % metricColors.length],
  }));

  // Calculate segment positions
  let currentAngle = -90; // Start at top
  const segments = metricsWithColors.map(metric => {
    const segmentLength = (metric.percentage / 100) * circumference;
    const startAngle = currentAngle;
    currentAngle += (metric.percentage / 100) * 360;

    return {
      ...metric,
      segmentLength,
      startAngle,
    };
  });

  const metricName = (name: string) => {
    switch (name) {
      case 'engagement':
        return 'Engagement';
      case 'stress':
        return 'Stress';
      case 'sleep':
        return 'Sleep';
      case 'exercise':
        return 'Steps';
      case 'activity_count':
        return 'Activity Count';
      case 'mood':
        return 'Mood';
      case 'adherence':
        return 'Adherence';
      default:
        return name;
    }
  };

  return (
    <div className={styles.scoreGraph}>
      <div className={styles.graphRow}>
        <div className={styles.circleContainer}>
          <svg width='160' height='160' className={styles.circleSvg}>
            {/* Background circle */}
            <circle cx='80' cy='80' r={radius} fill='none' stroke='#E5E5E5' strokeWidth='16' />
            {/* Dynamic segments */}
            {segments.map(segment => (
              <circle
                key={`${segment.name}-${segment.startAngle}-${segment.segmentLength}`}
                cx='80'
                cy='80'
                r={radius}
                fill='none'
                stroke={segment.color}
                strokeWidth='16'
                strokeDasharray={`${segment.segmentLength} ${circumference - segment.segmentLength}`}
                strokeLinecap='round'
                transform={`rotate(${segment.startAngle} 80 80)`}
              />
            ))}
          </svg>
          <div className={styles.scoreText}>
            <span className={styles.scoreLabel}>ViviHealth Score</span>
          </div>
        </div>
        <div className={`${styles.legend} ${metricsWithColors.length <= 3 ? styles.singleColumn : ''}`}>
          {metricsWithColors.map(metric => (
            <div key={`${metric.name}-${metric.color}-${metric.percentage}`} className={styles.legendItem}>
              <div className={styles.legendColor} style={{ backgroundColor: metric.color }} />
              <span>
                {metric.percentage}% {metricName(metric.name)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ViviHealthScoreGraph;
